import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../../config";

/* =========================================================
   EDIT POOL CARE SWAP LISTING
========================================================= */

const SERVICE_TASKS = [
  { key: "waterTesting", label: "Water testing" },
  { key: "chemicalBalancing", label: "Chemical balancing" },
  { key: "skimming", label: "Skimming" },
  { key: "vacuuming", label: "Vacuuming" },
  { key: "brushing", label: "Brushing" },
  { key: "filterCleaning", label: "Filter cleaning" },
  { key: "saltCellCleaning", label: "Salt cell cleaning" },
  { key: "chemicalSupplyIncluded", label: "Chemicals included" },
];

function EditPoolCareSwapListing() {
  const { listingId } = useParams();
  const navigate = useNavigate();



  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- load listing ---------------- */

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${API_BASE_URL}/api/poolcare/swap-listings/${listingId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (!res.ok || data.success === false) {
          setError("Failed to load swap listing");
          return;
        }

        setForm({
          ...data.listing,
          services: data.listing.services || {},
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load swap listing");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [listingId, API_BASE_URL]);

  /* ---------------- handlers ---------------- */

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleService = (key) => {
    setForm((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [key]: !prev.services[key],
      },
    }));
  };

  /* ---------------- submit ---------------- */

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/poolcare/swap-listings/${listingId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...form,
            serviceFee: form.serviceFee
              ? Number(form.serviceFee)
              : null,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        setError(data.error || "Failed to update swap listing");
        return;
      }

      navigate(`/poolcare/swaps/${listingId}`);
    } catch (err) {
      console.error(err);
      setError("Something went wrong saving the listing");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- render ---------------- */

  if (loading) return <p style={{ textAlign: "center" }}>Loading…</p>;
  if (error) return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;

  return (
    <div style={container}>
      <h2>✏️ Edit Pool Care Swap</h2>

      <form onSubmit={submit}>
        <Section title="Swap Locations">
          <Input label="From Suburb" name="swap_from_suburb" value={form.swap_from_suburb} onChange={update} />
          <Input label="From Postcode" name="swap_from_postcode" value={form.swap_from_postcode} onChange={update} />
          <Input label="From State" name="swap_from_state" value={form.swap_from_state} onChange={update} />

          <Input label="To Suburb" name="swap_to_suburb" value={form.swap_to_suburb} onChange={update} />
          <Input label="To Postcode" name="swap_to_postcode" value={form.swap_to_postcode} onChange={update} />
          <Input label="To State" name="swap_to_state" value={form.swap_to_state} onChange={update} />
        </Section>

        <Section title="Pool Details">
          <Input label="Pool Type" name="poolType" value={form.poolType} onChange={update} />
          <Input label="Pool Size" name="poolSize" value={form.poolSize} onChange={update} />
          <Input label="Pool Surface" name="poolSurface" value={form.poolSurface} onChange={update} />
          <Checkbox label="Spa Included" name="hasSpa" checked={form.hasSpa} onChange={update} />
        </Section>

        <Section title="Service Schedule">
          <Input label="Frequency" name="frequency" value={form.frequency} onChange={update} />
          <Input label="Service Day" name="serviceDay" value={form.serviceDay} onChange={update} />
          <Input label="Service Time" name="serviceTime" value={form.serviceTime} onChange={update} />
        </Section>

        <Section title="Included Services">
          <div style={grid}>
            {SERVICE_TASKS.map((s) => (
              <Checkbox
                key={s.key}
                label={s.label}
                checked={!!form.services[s.key]}
                onChange={() => toggleService(s.key)}
              />
            ))}
          </div>
        </Section>

        <Section title="Access">
          <Input label="Access Type" name="accessType" value={form.accessType} onChange={update} />
          <Checkbox label="Street Parking" name="streetParking" checked={form.streetParking} onChange={update} />
          <Checkbox label="Pets At Home" name="petsAtHome" checked={form.petsAtHome} onChange={update} />
          <Checkbox label="Alarm Code Required" name="alarmCodeRequired" checked={form.alarmCodeRequired} onChange={update} />
        </Section>

        <Section title="Swap Value">
          <Input label="Service Fee ($)" name="serviceFee" type="number" value={form.serviceFee || ""} onChange={update} />
        </Section>

        <button type="submit" disabled={saving} style={submitBtn}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- helpers & styles ---------------- */

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 25 }}>
    <h3>{title}</h3>
    {children}
  </div>
);

const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 10 }}>
    <label>
      {label}
      <input {...props} style={input} />
    </label>
  </div>
);

const Checkbox = ({ label, ...props }) => (
  <label style={checkbox}>
    <input type="checkbox" {...props} /> {label}
  </label>
);

const container = {
  maxWidth: 700,
  margin: "40px auto",
  background: "#fff",
  padding: 30,
  borderRadius: 12,
  boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
};

const input = { width: "100%", padding: 8, marginTop: 4 };
const checkbox = { display: "block", marginBottom: 6 };
const grid = { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 };
const submitBtn = {
  width: "100%",
  padding: 14,
  background: "#2e7d32",
  color: "#fff",
  fontSize: "1rem",
  fontWeight: "bold",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

export default EditPoolCareSwapListing;
